import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DownloadSimple, Trash, Plus, Spinner, FilePdf, FileDoc, FileText,
  Image as ImageIcon, VideoCamera, MicrophoneStage, ChatCircleText, Pencil,
  BookOpen, Textbox, Atom, Stack, Compass, SpeakerHigh, X, CaretLeft,
  GraduationCap, PaperPlaneTilt, ArrowsClockwise, CheckSquare, Square,
  Headphones, PencilLine
} from '@phosphor-icons/react';
import UploadProgress from '../components/UploadProgress';
import { chunkedUpload } from '../utils/chunkedUpload';
import ReadingPage from './ReadingPage';
import MaterialComments from '../components/MaterialComments';
import MaterialEditModal from '../components/MaterialEditModal';
import BuchUbungenPanel from '../components/BuchUbungenPanel';
import AudioRecorder from '../components/AudioRecorder';
import FilePreview from '../components/FilePreview';
import { fileUrl as buildFileUrl } from '../utils/fileUrl';
import ListeningExercises from '../components/ListeningExercises';
import NewBadge from '../components/NewBadge';
import { scheduleBadgeText } from '../lib/scheduleDraft';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1'];

// ── Tile definitions ─────────────────────────────────────────────────────────
// Top tiles under /materials. Plattform / Vokabeln / inner Kurs were removed
// in May 2026: Vokabeln lives in Üben already; Plattform + duplicate Kurs were
// pure redundant navigation. The 5 user-facing entry points to Kurs now live
// in the Home dashboard's KURS array (Videoanruf, Grammatik, Buch-Übungen,
// Analyse, Fragen) and we only keep here the leaves that still have a
// materials-list UI under them.
const TOP_TILES = [
  {
    cat: 'grammar',
    label: { de: 'Grammatik', es: 'Gramática' },
    desc:  { de: 'Zusammenfassungen der gelernten Grammatik', es: 'Resúmenes de la gramática aprendida' },
    icon: Atom, color: '#8B5A3C',
  },
  {
    cat: 'exercises',
    label: { de: 'Buch-Übungen', es: 'Ejercicios del libro' },
    desc:  { de: 'Übungsblätter und Aufgaben aus dem Lehrbuch', es: 'Fichas y tareas del libro' },
    icon: Pencil, color: '#AB1C24',
  },
  {
    cat: 'dudas',
    href: '/dudas',
    label: { de: 'Fragen', es: 'Dudas' },
    desc:  { de: 'Stell deine Frage — der Lehrer oder andere Schüler helfen dir', es: 'Haz tu pregunta — el profesor u otros alumnos te responderán' },
    icon: ChatCircleText, color: '#E06D53',
  },
];

// Sub-grids are no longer used (Hablar/Escuchar/Leer/Escribir live in Practicar
// as their own routes now). Kept empty for backwards-compat with deep links.
const SUBTILES = {};

// Labels for leaf categories that are NOT in TOP_TILES (they live as nav
// shortcuts under /uben). Keeps the in-page header in sync with the nav tile
// so users never see "speaking" / "listening" raw category keys.
const EXTRA_LABELS = {
  speaking:           { icon: MicrophoneStage, color: '#AB1C24', label: { de: 'Sprechen',   es: 'Hablar'   }, desc: { de: 'Audios von Alberto — hör zu und antworte mit deiner Aufnahme im Kommentar.', es: 'Audios de Alberto — escucha y responde con tu grabación en los comentarios.' } },
  listening:          { icon: Headphones,     color: '#D97706', label: { de: 'Hören',      es: 'Escuchar' }, desc: { de: 'Hörverstehen-Übungen vom Lehrer.', es: 'Ejercicios de comprensión auditiva del profesor.' } },
  reading_exercises:  { icon: BookOpen,       color: '#2F6B50', label: { de: 'Lesen',      es: 'Leer'     }, desc: { de: 'Leseverstehen-Texte mit Fragen.', es: 'Textos de comprensión lectora con preguntas.' } },
  writing:            { icon: PencilLine,     color: '#8B5A3C', label: { de: 'Schreiben',  es: 'Escribir' }, desc: { de: 'Schreibe einen Text — dein Lehrer korrigiert ihn.', es: 'Escribe un texto — tu profesor lo corregirá.' } },
};

// Students may upload only in these leaf categories.
// NOTE: speaking & listening are TEACHER-ONLY. Students only consume what the
// teacher uploads there (via the comments / response thread on each material).
const STUDENT_UPLOADABLE = new Set(['writing', 'vocabulary', 'exercises']);

// Legacy mapping for materials stored under OLD category names. We only keep
// categories here that no longer have a dedicated route in the new Üben layout
// (communication/pronunciation/plain "reading" all live inside Buch-Übungen now).
// IMPORTANT: speaking, listening, writing and reading_exercises now have their
// own routes under Üben — they must NOT be collapsed into 'exercises' or the
// materials the teacher uploads under /materials?tile=speaking will appear
// under Buch-Übungen instead.
const LEGACY_CAT = {
  communication: 'exercises',
  communication_oral: 'exercises',
  communication_written: 'exercises',
  pronunciation: 'exercises',
  reading: 'exercises',
};

const ACCEPT_TYPES = '.pdf,.docx,.doc,.pptx,.xlsx,.txt,.png,.jpg,.jpeg,.webp,.gif,.mp4,.webm,.mov,.m4v,.mp3,.wav,.ogg,.m4a';

const isImage = (f) => /\.(png|jpe?g|webp|gif)$/i.test(f.name || f.url || '') || (f.type || '').startsWith('image/');
// Detect audio vs. video by MIME *first* (unambiguous) and fall back to file
// extension only when MIME is missing. MediaRecorder on mobile usually saves
// audio recordings as audio/webm or audio/mp4 — without the MIME-first check,
// the .webm/.mp4 extension alone made isVideo match and we rendered the
// videocamera icon for what is actually a voice note. The previous regex also
// listed .webm under both videos and "not audio", so the audio-recording flow
// on mobile always looked like a stuck video. This split fixes that.
const isVideo = (f) => {
  const t = (f.type || '').toLowerCase();
  if (t.startsWith('video/')) return true;
  if (t.startsWith('audio/')) return false;
  return /\.(mp4|mov|m4v|webm)$/i.test(f.name || f.url || '');
};
const isAudio = (f) => {
  const t = (f.type || '').toLowerCase();
  if (t.startsWith('audio/')) return true;
  if (t.startsWith('video/')) return false;
  return /\.(mp3|wav|ogg|m4a|aac|opus|flac)$/i.test(f.name || f.url || '');
};
const fileUrl = (f) => buildFileUrl(f.url || f.content_url || '');

// ─────────────────────────────────────────────────────────────────────────────

function CategoryCard({ tile, lang, onClick, count, size = 'lg' }) {
  const Icon = tile.icon;
  const big = size === 'lg';
  return (
    <button
      onClick={onClick}
      data-testid={`tile-${tile.cat}`}
      className={`group relative bg-white rounded-2xl border border-stone-200 hover:border-[${tile.color}] hover:shadow-xl transition-all text-left p-5 sm:p-6 flex flex-col gap-3 min-h-[160px] sm:min-h-[200px]`}
      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
    >
      <div
        className={`${big ? 'w-14 h-14 sm:w-16 sm:h-16' : 'w-12 h-12'} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}
        style={{ backgroundColor: `${tile.color}15` }}
      >
        <Icon size={big ? 34 : 28} weight="duotone" style={{ color: tile.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className={`${big ? 'text-lg sm:text-xl' : 'text-base'} font-heading font-semibold text-[#292524] leading-tight`}>
          {tile.label[lang] || tile.label.de}
        </h3>
        {tile.desc && (
          <p className="text-xs sm:text-sm text-[#78716C] mt-1 leading-snug">
            {tile.desc[lang] || tile.desc.de}
          </p>
        )}
      </div>
      {typeof count === 'number' && (
        <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-wider bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">
          {count}
        </span>
      )}
    </button>
  );
}

function ThumbPreview({ f, small = false }) {
  const box = small ? 'w-16 h-16' : 'w-20 h-20';
  if (isImage(f)) {
    return (
      <a href={fileUrl(f)} target="_blank" rel="noopener noreferrer" className={`${box} rounded-lg overflow-hidden border border-stone-200 bg-stone-50 block flex-shrink-0`}>
        <img src={fileUrl(f)} alt={f.name || ''} className="w-full h-full object-cover" />
      </a>
    );
  }
  if (isVideo(f)) {
    return (
      <a href={fileUrl(f)} target="_blank" rel="noopener noreferrer" className={`${box} rounded-lg bg-[#0F1115] text-white flex items-center justify-center flex-shrink-0`}>
        <VideoCamera size={26} weight="fill" />
      </a>
    );
  }
  if (isAudio(f)) {
    // Slim, "Hören-like" inline audio preview — no big icon block, no fake
    // video chrome. preload='metadata' loads only the header so the duration
    // shows immediately without downloading the full file.
    return (
      <audio
        controls
        preload="metadata"
        src={fileUrl(f)}
        className="h-9 max-w-md w-full rounded-full flex-shrink-0"
        style={{ maxHeight: '36px' }}
      />
    );
  }
  const ext = (f.name || '').split('.').pop()?.toLowerCase();
  const Icon = ext === 'pdf' ? FilePdf : /docx?/.test(ext || '') ? FileDoc : FileText;
  return (
    <a href={fileUrl(f)} target="_blank" rel="noopener noreferrer" className={`${box} rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0`}>
      <Icon size={28} weight="duotone" className="text-[#AB1C24]" />
    </a>
  );
}

// ── Category detail view (list + composer) ───────────────────────────────────
function CategoryDetail({ catKey, lang, user, isTeacher, onBack, groups, myGroup }) {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [form, setForm] = useState({ title: '', description: '', files: [], levels: [user?.spanish_level || 'A2'], group_id: myGroup?.group_id || (groups[0]?.group_id || '') });
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadLabel, setUploadLabel] = useState('');
  const [editingMaterial, setEditingMaterial] = useState(null);

  const canUpload = isTeacher || STUDENT_UPLOADABLE.has(catKey);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/materials?limit=500`, { withCredentials: true });
      setMaterials(res.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Filter materials to this category (with legacy mapping)
  const visible = useMemo(() => {
    return materials.filter(m => {
      const c = LEGACY_CAT[m.category] || m.category;
      if (c !== catKey) return false;
      // In "Ejercicios del libro" each student only sees:
      //   - materials posted by staff (teacher/admin)
      //   - their own submissions
      // Teachers keep full visibility.
      if (!isTeacher && catKey === 'exercises') {
        const isStaff = m.author_role === 'admin' || m.author_role === 'teacher';
        const isMine  = m.created_by === user?.user_id;
        if (!isStaff && !isMine) return false;
      }
      return true;
    });
  }, [materials, catKey, isTeacher, user?.user_id]);

  // Group materials by class bucket. Materials tagged with the special
  // `__ALL__` group_id are visible in every class bucket (teachers see them
  // once per bucket so they can tell they're cross-class).
  const grouped = useMemo(() => {
    const buckets = {};
    const bucketList = isTeacher
      ? [...groups, { group_id: '__ALL__', name: lang === 'es' ? 'Todas las clases' : 'Alle Klassen', level: '' }, { group_id: '__nogroup', name: 'Ohne Klasse', level: '' }]
      : (myGroup ? [myGroup] : []);
    bucketList.forEach(b => { buckets[b.group_id] = []; });
    visible.forEach(m => {
      if (m.group_id === '__ALL__') {
        // Teachers get a dedicated "all classes" bucket; students see them in
        // their own bucket so they don't miss the content.
        if (isTeacher) { buckets['__ALL__'].push(m); }
        else if (myGroup && buckets[myGroup.group_id]) { buckets[myGroup.group_id].push(m); }
        return;
      }
      const gid = m.group_id && buckets[m.group_id] !== undefined ? m.group_id : (isTeacher ? '__nogroup' : null);
      if (gid !== null && buckets[gid]) buckets[gid].push(m);
    });
    return { bucketList, buckets };
  }, [visible, groups, myGroup, isTeacher, lang]);

  const handleMultiUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const uploaded = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadPct(0);
      setUploadLabel(`${file.name} (${i + 1}/${files.length})`);
      try {
        const res = await chunkedUpload(file, 'material', setUploadPct);
        uploaded.push({ url: res.storage_path, name: file.name, type: file.type || '', size: file.size });
      } catch (err) { toast.error(`${file.name}: ${err.response?.data?.detail || err.message}`); }
    }
    setForm(p => ({ ...p, files: [...p.files, ...uploaded] }));
    if (uploaded.length) toast.success(`${uploaded.length} Datei(en) hochgeladen`);
    setUploading(false);
    setUploadPct(0);
    setUploadLabel('');
    e.target.value = '';
  };

  const saveMaterial = async () => {
    if (!form.title.trim()) return toast.error('Titel erforderlich');
    if (!form.files.length) return toast.error('Mindestens eine Datei hochladen');
    try {
      // `datetime-local` gives us the teacher's LOCAL time (e.g. "2026-02-06T18:30").
      // Feeding that raw string to the backend was interpreted as UTC on the
      // read side, so a schedule set for "18:30 Berlin" only published at
      // "18:30 UTC" ≈ 19:30/20:30 Berlin — the teacher thought the schedule
      // "didn't work". `new Date(...)` correctly treats `datetime-local` as
      // local time; `toISOString()` normalises to UTC so both writes and
      // reads compare against the same clock.
      const sched = scheduledAt.trim()
        ? new Date(scheduledAt).toISOString()
        : '';
      await axios.post(`${API}/materials`, {
        title: form.title.trim(),
        description: form.description.trim(),
        levels: form.levels.length ? form.levels : ['A2'],
        category: catKey,
        content_url: form.files[0].url,
        content_type: (form.files[0].name || '').split('.').pop().toLowerCase(),
        files: form.files,
        group_id: form.group_id || '',
        scheduled_at: sched,
      }, { withCredentials: true });
      toast.success(sched ? 'Für den geplanten Zeitpunkt eingeplant' : 'Material erstellt');
      setForm({ title: '', description: '', files: [], levels: [user?.spanish_level || 'A2'], group_id: myGroup?.group_id || (groups[0]?.group_id || '') });
      setScheduledAt('');
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const deleteMat = async (id) => {
    if (!window.confirm(isTeacher
      ? 'Wirklich in den Papierkorb verschieben? Du kannst es dort jederzeit wiederherstellen.'
      : 'Löschen?'
    )) return;
    try { await axios.delete(`${API}/materials/${id}`, { withCredentials: true }); load(); }
    catch { toast.error('Fehler'); }
  };

  // Resend / reassign an existing material to additional classes. Limited to
  // the four "skill" categories below — other categories aren't class-specific
  // in the same way.
  const RESENDABLE = new Set(['speaking', 'listening', 'reading_exercises', 'writing', 'grammar', 'exercises', 'vocabulary']);
  const [resendDialog, setResendDialog] = useState(null); // { material, selected: Set<string> }
  const [resendBusy, setResendBusy] = useState(false);

  const openResend = (mat) => setResendDialog({ material: mat, selected: new Set() });
  const toggleResendTarget = (gid) => {
    setResendDialog(d => {
      if (!d) return d;
      const s = new Set(d.selected);
      if (s.has(gid)) s.delete(gid); else s.add(gid);
      return { ...d, selected: s };
    });
  };
  const submitResend = async () => {
    if (!resendDialog) return;
    const targets = Array.from(resendDialog.selected);
    if (targets.length === 0) { toast.error('Bitte mindestens eine Klasse auswählen'); return; }
    setResendBusy(true);
    try {
      const res = await axios.post(`${API}/materials/${resendDialog.material.material_id}/resend`,
        { group_ids: targets }, { withCredentials: true });
      const created = res.data?.count ?? 0;
      const skipped = (res.data?.skipped_group_ids || []).length;
      if (created > 0) toast.success(`${created} Kopie${created === 1 ? '' : 'n'} angelegt`);
      if (skipped > 0) toast.info(`${skipped} Klasse${skipped === 1 ? '' : 'n'} übersprungen — bereits zugewiesen`);
      setResendDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim erneuten Zuweisen');
    } finally {
      setResendBusy(false);
    }
  };

  const allSubs = Object.values(SUBTILES).flat();
  const tile = TOP_TILES.find(t => t.cat === catKey) || allSubs.find(t => t.cat === catKey) || EXTRA_LABELS[catKey];
  const title = tile?.label?.[lang] || catKey;
  const desc  = tile?.desc?.[lang] || '';
  const TileIcon = tile?.icon;
  const tileColor = tile?.color || '#AB1C24';

  // ── Hero-layout switch ─────────────────────────────────────────────────
  // Grammatik + Buch-Übungen used to render the same plain header as every
  // other category. Alberto asked for a warmer, more editorial look for these
  // two entry points, so they get a large hero card, decorative motifs and
  // richer material cards. All existing functionality (upload, edit, resend,
  // schedule, delete, comments/submissions panel) stays identical.
  const useHeroLayout = catKey === 'grammar' || catKey === 'exercises';
  const heroAccent = catKey === 'grammar' ? '#8B5A3C' : '#AB1C24';
  const heroTag = catKey === 'grammar'
    ? (lang === 'es' ? 'Referencia · Curso' : 'Referenz · Kurs')
    : (lang === 'es' ? 'Práctica · Libro' : 'Übungen · Buch');
  const heroKicker = catKey === 'grammar'
    ? (lang === 'es' ? 'Cada regla, ordenada. Vuelve cuando la necesites.' : 'Jede Regel, sortiert. Komm zurück, wenn du sie brauchst.')
    : (lang === 'es' ? 'Los ejercicios del libro con la corrección de tu profesor.' : 'Die Buchübungen mit persönlicher Korrektur deines Lehrers.');
  // Count only what will actually render (student's own bucket or the sum
  // across teacher buckets). Using `visible.length` here would over-count for
  // students because materials assigned to other classes are filtered out on
  // the render side but still counted in `visible`.
  const totalCount = Object.values(grouped.buckets).reduce((n, arr) => n + (arr?.length || 0), 0);
  const newCount = Object.values(grouped.buckets).reduce((n, arr) => n + (arr || []).filter(m => {
    const t = new Date(m.created_at || 0).getTime();
    return t && Date.now() - t < 7 * 24 * 60 * 60 * 1000;
  }).length, 0);

  return (
    <div className="space-y-4">
      {useHeroLayout ? (
        <div
          className="relative overflow-hidden rounded-3xl shadow-sm border border-stone-200"
          data-testid={`hero-${catKey}`}
          style={{
            background: `linear-gradient(135deg, ${heroAccent}1a 0%, #FAF9F6 55%, #ffffff 100%)`,
          }}
        >
          {/* Decorative motifs — soft circles that echo the accent colour.
              Purely visual, hidden from screen readers. */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-20"
               style={{ background: heroAccent }} />
          <div aria-hidden="true" className="pointer-events-none absolute bottom-0 right-24 w-24 h-24 rounded-full opacity-10"
               style={{ background: heroAccent }} />
          <div aria-hidden="true" className="pointer-events-none absolute top-8 right-40 w-3 h-3 rounded-full"
               style={{ background: heroAccent, opacity: 0.4 }} />

          <div className="relative p-6 sm:p-8 flex items-start gap-4 sm:gap-6">
            {TileIcon && (
              <div
                className="hidden sm:flex items-center justify-center w-20 h-20 rounded-2xl shrink-0 shadow-md"
                style={{
                  background: `linear-gradient(160deg, ${heroAccent} 0%, ${heroAccent}bb 100%)`,
                  boxShadow: `0 12px 30px -12px ${heroAccent}55`,
                }}
              >
                <TileIcon size={44} weight="duotone" color="white" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span
                  className="text-[10px] font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full"
                  style={{ background: `${heroAccent}18`, color: heroAccent }}
                >
                  {heroTag}
                </span>
                {totalCount > 0 && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">
                    {totalCount} {lang === 'es' ? (totalCount === 1 ? 'entrada' : 'entradas') : (totalCount === 1 ? 'Eintrag' : 'Einträge')}
                  </span>
                )}
                {newCount > 0 && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    {newCount} {lang === 'es' ? (newCount === 1 ? 'nueva' : 'nuevas') : 'neu'}
                  </span>
                )}
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#292524] tracking-tight flex items-center gap-3">
                {TileIcon && <TileIcon size={30} weight="duotone" style={{ color: heroAccent }} className="sm:hidden" />}
                {title}
              </h2>
              <p className="mt-2 text-[15px] text-[#57534E] leading-relaxed max-w-xl">{heroKicker}</p>
              {desc && (
                <p className="mt-1 text-sm text-[#78716C] italic max-w-xl">{desc}</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524] flex items-center gap-2">
            {TileIcon && <TileIcon size={28} weight="duotone" style={{ color: tileColor }} />}
            {title}
          </h2>
          {desc && <p className="text-sm text-[#57534E] mt-1">{desc}</p>}
        </div>
      )}

      {canUpload && !showForm && (
        <button
          onClick={() => setShowForm(true)}
          className={useHeroLayout
            ? 'inline-flex items-center gap-2 text-white px-5 py-2.5 rounded-full font-semibold shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5'
            : 'inline-flex items-center gap-2 bg-[#AB1C24] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#8A151C]'}
          style={useHeroLayout ? { background: heroAccent, boxShadow: `0 8px 20px -8px ${heroAccent}88` } : undefined}
          data-testid={`mat-add-${catKey}`}
        ><Plus size={16} weight="bold" /> {lang === 'es' ? 'Añadir material' : 'Material hinzufügen'}</button>
      )}

      {canUpload && showForm && (
        <div className="bg-white rounded-xl border border-stone-200 p-4 space-y-3" data-testid={`mat-form-${catKey}`}>
          <Input placeholder="Titel" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} data-testid="mat-form-title" />
          <Input placeholder="Beschreibung (optional)" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          {isTeacher && (
            <>
              <div>
                <p className="text-xs font-medium text-[#57534E] mb-1.5">Niveaus</p>
                <div className="flex flex-wrap gap-1.5">
                  {LEVELS.map(lvl => {
                    const on = form.levels.includes(lvl);
                    return (
                      <button key={lvl} type="button"
                        onClick={() => setForm(p => ({ ...p, levels: on ? p.levels.filter(l => l !== lvl) : [...p.levels, lvl] }))}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${on ? 'bg-[#AB1C24] text-white border-[#AB1C24]' : 'bg-white text-[#57534E] border-stone-300'}`}
                      >{lvl}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-[#57534E] mb-1.5">{lang === 'es' ? 'Clase' : 'Klasse'}</p>
                <select value={form.group_id || ''} onChange={e => setForm(p => ({ ...p, group_id: e.target.value }))}
                  className="text-sm border border-stone-300 rounded-md px-2 py-1.5 bg-white" data-testid="mat-form-group">
                  <option value="">— {lang === 'es' ? 'Sin clase' : 'Keine Klasse'} —</option>
                  <option value="__ALL__">
                    {lang === 'es' ? 'Todas las clases' : 'Alle Klassen'}
                  </option>
                  {groups.map(g => <option key={g.group_id} value={g.group_id}>{g.name} · {g.level}</option>)}
                </select>
              </div>
              <div>
                <p className="text-xs font-medium text-[#57534E] mb-1.5">
                  {lang === 'es' ? 'Programar para (opcional)' : 'Veröffentlichen am (optional)'}
                </p>
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={e => setScheduledAt(e.target.value)}
                  className="text-sm border border-stone-300 rounded-md px-2 py-1.5 bg-white"
                  data-testid="mat-form-scheduled-at"
                />
              </div>
            </>
          )}
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-stone-300 rounded-lg cursor-pointer hover:bg-stone-50 text-sm font-medium">
            <DownloadSimple size={16} />
            {uploading ? `${uploadPct}%` : (lang === 'es' ? 'Elegir archivos' : 'Dateien wählen')}
            <input type="file" className="hidden" multiple accept={ACCEPT_TYPES} onChange={handleMultiUpload} />
          </label>

          {/* Audio recorder for Hablar / Escuchar categories */}
          {(catKey === 'speaking' || catKey === 'listening') && (
            <AudioRecorder
              testId={`form-audio-${catKey}`}
              maxSeconds={600}
              onUploaded={(f) => setForm(p => ({ ...p, files: [...p.files, f] }))}
            />
          )}

          <UploadProgress active={uploading} percent={uploadPct} label={uploadLabel} />
          {form.files.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {form.files.map((f, i) => (
                <div key={i} className="relative">
                  <FilePreview file={f} lang={lang} />
                  <button onClick={() => setForm(p => ({ ...p, files: p.files.filter((_, k) => k !== i) }))}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#AB1C24] text-white flex items-center justify-center">
                    <X size={10} weight="bold" />
                  </button>
                  <p className="text-[10px] text-[#78716C] truncate max-w-[64px] mt-1">{f.name}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={saveMaterial} disabled={uploading} className="bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="mat-form-save">{lang === 'es' ? 'Publicar' : 'Speichern'}</Button>
            <Button variant="outline" onClick={() => { setShowForm(false); setForm(p => ({ ...p, title: '', description: '', files: [] })); }}>{lang === 'es' ? 'Cancelar' : 'Abbrechen'}</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Spinner size={32} className="animate-spin text-[#AB1C24]" /></div>
      ) : visible.length === 0 ? (
        useHeroLayout ? (
          <div
            className="relative overflow-hidden rounded-3xl border-2 border-dashed p-10 text-center"
            style={{ borderColor: `${heroAccent}55`, background: `${heroAccent}08` }}
            data-testid={`empty-${catKey}`}
          >
            <div
              className="mx-auto mb-3 w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{ background: `${heroAccent}22` }}
            >
              {TileIcon && <TileIcon size={30} weight="duotone" style={{ color: heroAccent }} />}
            </div>
            <p className="font-heading text-lg font-semibold text-[#292524]">
              {canUpload
                ? (lang === 'es' ? 'Aún no hay nada aquí' : 'Noch nichts vorhanden')
                : (lang === 'es' ? 'Tu profesor aún no ha subido nada' : 'Dein Lehrer hat noch nichts hochgeladen')}
            </p>
            <p className="text-sm text-[#78716C] mt-1">
              {canUpload
                ? (lang === 'es' ? 'Usa el botón de arriba para añadir el primer material' : 'Nutze den Button oben, um das erste Material hinzuzufügen')
                : (lang === 'es' ? 'Vuelve pronto — te avisaremos cuando llegue algo nuevo.' : 'Schau bald wieder vorbei — du bekommst eine Benachrichtigung.')}
            </p>
          </div>
        ) : (
          <p className="text-center text-[#78716C] py-10 bg-white rounded-xl border border-stone-200">
            {canUpload ? (lang === 'es' ? 'Aún no hay material aquí. Usa el botón de arriba.' : 'Noch kein Material. Lade etwas hoch.') : (lang === 'es' ? 'Tu profesor aún no ha subido material aquí.' : 'Noch kein Material.')}
          </p>
        )
      ) : (
        <div className="space-y-4">
          {grouped.bucketList.map(b => {
            const list = grouped.buckets[b.group_id] || [];
            if (!list.length && !isTeacher) return null;
            return (
              <div key={b.group_id} className={useHeroLayout ? 'bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm' : 'bg-white rounded-xl border border-stone-200 overflow-hidden'}>
                {isTeacher && (
                  <div className="px-4 py-2 bg-[#FAF9F6] border-b border-stone-200 flex items-center gap-2">
                    <GraduationCap size={16} weight="duotone" className="text-violet-600" />
                    <span className="font-semibold text-sm text-[#292524]">{b.name}</span>
                    {b.level && <span className={`level-badge ${b.level}`}>{b.level}</span>}
                    <span className="ml-auto text-xs text-[#78716C]">{list.length} {list.length === 1 ? 'Eintrag' : 'Einträge'}</span>
                  </div>
                )}
                <div className={useHeroLayout ? 'divide-y divide-stone-100 p-2 sm:p-3 space-y-2' : 'divide-y divide-stone-100'}>
                  {list.map(mat => {
                    const files = (mat.files && mat.files.length) ? mat.files : (mat.content_url ? [{ url: mat.content_url, name: mat.title, type: mat.content_type }] : []);
                    const canDelete = isTeacher || mat.created_by === user?.user_id;
                    return (
                      <article
                        key={mat.material_id}
                        className={useHeroLayout
                          ? 'relative pl-4 pr-3 py-4 sm:pl-5 sm:pr-4 sm:py-5 rounded-xl bg-white border border-stone-100 hover:border-stone-200 hover:shadow-md transition-all group'
                          : 'p-4'}
                        style={useHeroLayout ? { borderLeft: `4px solid ${heroAccent}` } : undefined}
                        data-testid={`material-${mat.material_id}`}
                      >
                        <header className="flex items-start justify-between gap-3 mb-2">
                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-[#292524] flex items-center gap-1.5 flex-wrap">
                              <span>{mat.title}</span>
                              <NewBadge createdAt={mat.created_at} authorRole={mat.author_role} />
                              {isTeacher && mat.scheduled_at && new Date(mat.scheduled_at) > new Date() && (
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full"
                                  title={`Wird veröffentlicht: ${new Date(mat.scheduled_at).toLocaleString('de-DE')}`}
                                  data-testid={`scheduled-badge-${mat.material_id}`}
                                >
                                  {scheduleBadgeText(mat.scheduled_at)}
                                </span>
                              )}
                            </h3>
                            {mat.description && <p className="text-sm text-[#78716C] mt-0.5">{mat.description}</p>}
                            {mat.cloned_from && isTeacher && (
                              <p className="text-[11px] text-violet-600 mt-0.5 flex items-center gap-1" data-testid={`cloned-badge-${mat.material_id}`}>
                                <ArrowsClockwise size={11} weight="bold" />
                                Erneut zugewiesen{mat.cloned_from_title ? ` aus „${mat.cloned_from_title}"` : ''}
                              </p>
                            )}
                            {mat.author_name && mat.author_role !== 'admin' && mat.author_role !== 'teacher' && (
                              <p className="text-[11px] text-[#A8A29E] mt-0.5">— {mat.author_name}</p>
                            )}
                          </div>
                          {isTeacher && (
                            <button
                              onClick={() => setEditingMaterial(mat)}
                              className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-500 hover:text-amber-700 flex-shrink-0"
                              title="Material bearbeiten"
                              data-testid={`edit-${mat.material_id}`}
                            >
                              <Pencil size={14} weight="bold" />
                            </button>
                          )}
                          {isTeacher && RESENDABLE.has(catKey) && (
                            <button
                              onClick={() => openResend(mat)}
                              className="p-1.5 rounded-lg hover:bg-violet-50 text-violet-500 hover:text-violet-700 flex-shrink-0"
                              title="An weitere Klassen erneut zuweisen"
                              data-testid={`resend-${mat.material_id}`}
                            >
                              <PaperPlaneTilt size={16} weight="duotone" />
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => deleteMat(mat.material_id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 flex-shrink-0" data-testid={`delete-${mat.material_id}`}>
                              <Trash size={16} />
                            </button>
                          )}
                        </header>
                        {files.length > 0 && (
                          <div className="space-y-2">
                            {files.map((f, i) => (
                              <FilePreview key={i} file={f} lang={lang} />
                            ))}
                          </div>
                        )}
                        {catKey === 'exercises'
                          ? <BuchUbungenPanel materialId={mat.material_id} isDe={lang !== 'es'} />
                          : <MaterialComments materialId={mat.material_id} canParticipate={true} category={catKey} />}
                      </article>
                    );
                  })}
                  {list.length === 0 && isTeacher && (
                    <p className="text-sm text-[#A8A29E] italic px-4 py-3">Noch leer.</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Resend-to-other-classes dialog (teacher only, speaking/listening/reading_exercises/writing) */}
      <MaterialEditModal
        material={editingMaterial}
        open={!!editingMaterial}
        onClose={() => setEditingMaterial(null)}
        onSaved={(updated) => {
          setMaterials(prev => prev.map(m => m.material_id === updated.material_id ? { ...m, ...updated } : m));
          setEditingMaterial(null);
        }}
      />
      {resendDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !resendBusy && setResendDialog(null)} data-testid="resend-dialog">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-stone-200 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-heading font-bold text-lg text-[#292524] flex items-center gap-2">
                  <ArrowsClockwise size={20} weight="duotone" className="text-violet-600" />
                  Material erneut zuweisen
                </h3>
                <p className="text-sm text-[#78716C] mt-1 truncate">„{resendDialog.material.title || '(ohne Titel)'}"</p>
              </div>
              <button onClick={() => !resendBusy && setResendDialog(null)} className="p-1 rounded-lg hover:bg-stone-100"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-[#57534E]">
                Wähle die Klassen, an die du dieses Material zusätzlich senden möchtest. Pro Klasse wird eine eigene Kopie mit eigenem Kommentar-Thread erstellt. Klassen, die das Material bereits haben, werden übersprungen.
              </p>
              <div className="space-y-1 max-h-[280px] overflow-y-auto bg-[#FAF9F6] rounded-xl p-2">
                {/* __ALL__ pseudo-target */}
                <button
                  onClick={() => toggleResendTarget('__ALL__')}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white text-left"
                  data-testid="resend-target-__ALL__"
                >
                  {resendDialog.selected.has('__ALL__')
                    ? <CheckSquare size={20} weight="fill" className="text-violet-600 flex-shrink-0" />
                    : <Square size={20} className="text-stone-300 flex-shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm text-[#292524]">Alle Klassen (klassenübergreifend)</p>
                    <p className="text-xs text-[#78716C]">Für jeden Schüler sichtbar — unabhängig von der Klasse.</p>
                  </div>
                </button>
                {groups.map(g => (
                  <button
                    key={g.group_id}
                    onClick={() => toggleResendTarget(g.group_id)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white text-left"
                    data-testid={`resend-target-${g.group_id}`}
                  >
                    {resendDialog.selected.has(g.group_id)
                      ? <CheckSquare size={20} weight="fill" className="text-violet-600 flex-shrink-0" />
                      : <Square size={20} className="text-stone-300 flex-shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm text-[#292524] truncate">{g.name || g.group_id}</p>
                      <p className="text-xs text-[#78716C]">
                        {g.level && <span className="mr-2">{g.level}</span>}
                        {(g.member_count ?? (g.members || []).length)} Schüler
                      </p>
                    </div>
                  </button>
                ))}
                {groups.length === 0 && (
                  <p className="text-sm text-[#A8A29E] italic text-center py-4">Noch keine Klassen angelegt.</p>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-stone-200 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setResendDialog(null)} disabled={resendBusy} data-testid="resend-cancel">
                Abbrechen
              </Button>
              <Button
                onClick={submitResend}
                disabled={resendBusy || resendDialog.selected.size === 0}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="resend-submit"
              >
                {resendBusy ? <Spinner size={16} className="animate-spin mr-2" /> : <PaperPlaneTilt size={16} className="mr-2" weight="fill" />}
                {resendBusy
                  ? 'Wird gesendet…'
                  : `An ${resendDialog.selected.size} Klasse${resendDialog.selected.size === 1 ? '' : 'n'} senden`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function MaterialsPage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const isTeacher = user?.role === 'admin' || user?.role === 'teacher';
  const [groups, setGroups] = useState([]);
  const [myGroup, setMyGroup] = useState(null);
  const [selected, setSelected] = useState(null);
  const [subSelected, setSubSelected] = useState(null);
  const [counts, setCounts] = useState({});

  // Deep-link support: /materials?tile=speaking opens that leaf directly
  useEffect(() => {
    const tileParam = new URLSearchParams(location.search).get('tile');
    if (!tileParam) { setSelected(null); setSubSelected(null); return; }
    const top = TOP_TILES.find(t => t.cat === tileParam);
    if (top) { setSelected(top); setSubSelected(null); return; }
    // leaf of a sub-group
    for (const sub of Object.values(SUBTILES)) {
      const leaf = (sub || []).find(t => t.cat === tileParam);
      if (leaf) { setSelected(TOP_TILES.find(t => (SUBTILES[t.subTiles] || []).includes(leaf))); setSubSelected(leaf); return; }
    }
    // Unknown tile: treat as raw category leaf (e.g. speaking/listening/reading_exercises)
    setSelected({ cat: tileParam });
    setSubSelected(null);
  }, [location.search]);

  useEffect(() => {
    if (isTeacher) {
      axios.get(`${API}/groups`, { withCredentials: true }).then(r => setGroups(r.data || [])).catch(() => {});
    } else {
      axios.get(`${API}/me/group`, { withCredentials: true }).then(r => {
        const g = r.data || null;
        setMyGroup(g);
        setGroups(g ? [g] : []);
      }).catch(() => {});
    }
    // Pre-compute counts per category for badges
    axios.get(`${API}/materials?limit=500`, { withCredentials: true }).then(r => {
      const c = {};
      (r.data || []).forEach(m => {
        const real = LEGACY_CAT[m.category] || m.category;
        c[real] = (c[real] || 0) + 1;
      });
      setCounts(c);
    }).catch(() => {});
  }, [isTeacher]);

  // If user arrived here via a deep-link (?tile=xxx) — e.g. from Home's
  // quick-access — the natural Zurück target is the previous page (Home),
  // not the /materials hub grid.
  const cameFromDeepLink = !!new URLSearchParams(location.search).get('tile');
  const goBack = () => {
    if (cameFromDeepLink) { navigate(-1); return; }
    if (subSelected) setSubSelected(null);
    else setSelected(null);
  };

  // Leaf = currently viewed category (may come from a sub-tile)
  const leafCat = subSelected?.cat || (selected && !selected.subTiles ? selected.cat : null);

  // Special page: ReadingPage for "Leer" category
  if (leafCat === 'reading_exercises') {
    return (
      <div className="space-y-3" data-testid="materials-reading">
        <ReadingPage />
      </div>
    );
  }

  // Special page: dedicated MC + audio flow for "Hören / Escuchar".
  // Exercises created here ALSO appear in Spiele → Hörverstehen, classified
  // by CEFR level — see /app/frontend/src/components/ListeningExercises.js
  if (leafCat === 'listening') {
    return (
      <div className="space-y-3" data-testid="materials-listening">
        <ListeningExercises user={user} lang={lang} />
      </div>
    );
  }

  if (leafCat) {
    return (
      <CategoryDetail
        catKey={leafCat}
        lang={lang}
        user={user}
        isTeacher={isTeacher}
        groups={groups}
        myGroup={myGroup}
        onBack={goBack}
      />
    );
  }

  // Sub-tile grid (Oral → Hablar/Escuchar; Escrita → Escribir/Leer)
  if (selected?.subTiles) {
    const tiles = SUBTILES[selected.subTiles] || [];
    return (
      <div className="space-y-5" data-testid="materials-subgrid">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524]">{selected.label[lang]}</h2>
          <p className="text-sm text-[#57534E] mt-1">{selected.desc[lang]}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {tiles.map(t => (
            <CategoryCard key={t.cat} tile={t} lang={lang} onClick={() => setSubSelected(t)} count={counts[t.cat] || 0} />
          ))}
        </div>
      </div>
    );
  }

  // Top-level grid
  return (
    <div className="space-y-5" data-testid="materials-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524] flex items-center gap-2">
          <GraduationCap size={28} weight="duotone" className="text-[#AB1C24]" />
          {lang === 'es' ? 'Curso' : 'Kurs'}
        </h1>
        {!isTeacher && myGroup && (
          <div className="inline-flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-full px-3 py-1.5 mt-2" data-testid="my-group-badge">
            <GraduationCap size={16} className="text-violet-600" weight="fill" />
            <span className="text-sm font-semibold text-violet-700">{myGroup.name}</span>
            {myGroup.schedule && <span className="text-xs text-violet-600/80">· {myGroup.schedule}</span>}
          </div>
        )}
        {!isTeacher && !myGroup && (
          <p className="text-sm text-amber-700 mt-1">{lang === 'es' ? 'Aún no te han asignado a una clase.' : 'Noch keiner Klasse zugewiesen.'}</p>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
        {TOP_TILES.map(t => (
          <CategoryCard
            key={t.cat}
            tile={t}
            lang={lang}
            onClick={() => {
              if (t.href) { navigate(t.href); return; }
              setSelected(t);
            }}
            count={t.href ? undefined : (t.subTiles
              ? (SUBTILES[t.subTiles] || []).reduce((s, st) => s + (counts[st.cat] || 0), 0)
              : (counts[t.cat] || 0))}
          />
        ))}
      </div>
    </div>
  );
}
